import { ChangeEvent, useState } from 'react';
import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { DefaultButton, FlexStack, Icon } from '~/components/atomic';
import { BoxText, FullScreenLoading } from '~/components/common';
import { Header } from '~/components/common/Header';
import { getPreSignedUrl } from '~/api';
import { useModal } from '~/hooks';
import { ModalParams } from '~/types';
import { checkCharacterFile } from '~/utils';
import { MEGABYTE } from '~/constants/size';
import document from '~/assets/img/idolMaster/document.png';
import useCreateNewCharacterStore from '~/store/useCreateNewCharacterStore';
import useToast from '~/store/useToast';
import * as Main from '../SelectCharacter/SelectCharacter.styled';
import * as S from './CustomizeUpload.styled';
import ProcessBox from './ProcessBox';

interface CustomizeUploadProps {
  onPrevStep: () => void;
  onNextStep: () => void;
}

export default function CustomizeUpload({
  onPrevStep,
  onNextStep,
}: CustomizeUploadProps) {
  const { openModal } = useModal();
  const [isPending, setIsPending] = useState(false);
  const { customizeUploadFile, setUploadFile, setUserUploadUrl } =
    useCreateNewCharacterStore((state) => ({
      customizeUploadFile: state.customizeUpload.file,
      setUploadFile: state.setUploadFile,
      setUserUploadUrl: state.setUserUploadUrl,
    }));
  const [processState, setProcessState] = useState<'' | 'error' | 'success'>(
    '',
  );
  const { showToast } = useToast();

  const handleUploadClick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setProcessState('error');
      return;
    }

    const fileType = file.type;
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > 120 * MEGABYTE) {
      showToast('error', 'The file is too large');
      setProcessState('error');
      return;
    }

    if (
      fileType !== 'model/gltf-binary' &&
      fileType !== 'model/gltf+json' &&
      fileExtension !== 'glb' &&
      fileExtension !== 'gltf'
    ) {
      const useModalParams: ModalParams = {
        title: 'File type error',
        content: 'Invalid file type. Only .glb and .gltf files are allowed.',
        type: 'single',
        buttonName: 'OK',
      };
      openModal(useModalParams);
      setProcessState('error');
      return;
    }

    setProcessState('success');
    setUploadFile(file);
    e.target.value = '';
  };

  const handleDeleteClick = () => {
    setUploadFile(undefined);
  };

  const handleNextClick = async () => {
    const type = checkCharacterFile(customizeUploadFile?.name);
    if (customizeUploadFile && type) {
      try {
        setIsPending(true);
        const { data, status } = await getPreSignedUrl({
          file_type: type,
          generate_type: 'user',
        });
        if (status === 200) {
          setUserUploadUrl(data.file_path);
          await uploadFileToS3(data.pre_signed_url);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsPending(false);
      }
    }
  };

  const uploadFileToS3 = async (s3url: string) => {
    if (customizeUploadFile) {
      try {
        const formData = new FormData();
        formData.append('file', customizeUploadFile);
        const { data } = await axios.put(s3url, formData.get('file'), {
          headers: { 'Content-Type': customizeUploadFile.type },
        });
        onNextStep();
      } catch {
        console.error('error');
      }
    }
  };

  return (
    <>
      {isPending && <FullScreenLoading />}

      <Header.Root>
        <Header.Section>
          <Header.PrevButton onPrevClick={onPrevStep} />
          <Header.Title>Upload avatar</Header.Title>
        </Header.Section>
      </Header.Root>

      <Main.Container>
        <FlexStack spacing={16}>
          <S.CharacterUploadCard
            as={customizeUploadFile ? 'div' : 'label'}
            className={customizeUploadFile ? 'on' : ''}
          >
            {customizeUploadFile ? (
              <FlexStack
                css={css`
                  align-items: center;
                `}
              >
                <FlexStack
                  css={css`
                    gap: 0;
                    align-items: center;
                  `}
                >
                  <S.DocumentImg src={document} alt='document' />
                  <S.Description>{customizeUploadFile.name}</S.Description>
                </FlexStack>

                <S.FileName onClick={handleDeleteClick} className='on'>
                  Remove
                </S.FileName>
              </FlexStack>
            ) : (
              <>
                <input
                  type='file'
                  accept='.glb, .gltf'
                  onChange={handleUploadClick}
                />
                <FlexStack
                  spacing={16}
                  css={css`
                    align-items: center;
                  `}
                >
                  <FlexStack
                    css={css`
                      align-items: center;
                    `}
                  >
                    <Icon iconName='Add' size={28} />
                    <S.Description>Import Shape key file</S.Description>
                  </FlexStack>
                  <FlexStack
                    direction='row'
                    css={css`
                      align-items: center;
                    `}
                  >
                    <S.FileName>Only for</S.FileName>
                    <div>
                      <BoxText>GLB</BoxText> <BoxText>GLTF</BoxText>
                    </div>
                  </FlexStack>
                </FlexStack>
              </>
            )}
          </S.CharacterUploadCard>

          {processState === 'error' && (
            <ProcessBox
              type='error'
              title='Failed'
              description='Your file failed to load, please refer to the  upload guide.'
            />
          )}
          {processState === 'success' && (
            <ProcessBox
              type='success'
              title='Done'
              description='Your character is ready.'
            />
          )}
        </FlexStack>

        <Main.BottomWrapper>
          <DefaultButton
            onClick={handleNextClick}
            disabled={!customizeUploadFile}
          >
            <p>Next</p>
            <Icon iconName='ArrowRight' />
          </DefaultButton>
        </Main.BottomWrapper>
      </Main.Container>
    </>
  );
}
